import { useCustom, useCustomMutation, useTranslate } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Radio,
  Space,
  Spin,
  Typography,
} from 'antd';
import { format, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import { DateFnsDatePicker } from '../../components/Pickers';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { usePickerLocale } from '../homework/usePickerLocale';
import {
  FORM_FIELDS,
  fillAnswers,
  scaleSteps,
  type FillDraft,
  type WorksheetFieldData,
  type WorksheetFormData,
} from './hubModel';

/**
 * E34 · SubmitWorksheet (in session) — "Client Hub — Worksheets & documents — Fill with the client
 * (section)" (cc-homework, chapter "Worksheets — Responses & documents (portal, E34)", buildOrder 28):
 * "In session (or on the phone with a client who has no app), the therapist fills the form WITH the
 * client — the same SubmitWorksheet, the therapist as performer. A consent filled this way still needs
 * the client's own signature on the phone." The form is the client's own (`worksheetForm`, the version the
 * assignment pinned) rendered with the Builder's eight question types; Submit → `submitWorksheet` (the
 * server mints the submission id and stamps the therapist as `performer`). A refusal is shown verbatim
 * (`homework.errors.WORKSHEET_*`, already in the therapist's language), in the modal.
 */
type Props = {
  assignmentId: string | null;
  clientName: string;
  onClose: () => void;
  onDone: () => void;
};

export const WorksheetFillModal = ({ assignmentId, clientName, onClose, onDone }: Props) => {
  const t = useTranslate();
  const pickerLocale = usePickerLocale();
  const [draft, setDraft] = useState<FillDraft>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    setDraft({});
    setProblem(null);
  }, [assignmentId]);

  const { data, isFetching, isError } = useCustom<WorksheetFormData>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'worksheetForm',
      fields: FORM_FIELDS,
      variables: { assignmentId: { name: 'assignmentId', type: 'ID!', value: assignmentId } },
    },
    queryOptions: { enabled: !!assignmentId, retry: false },
  });
  const form = assignmentId ? data?.data ?? null : null;

  const { mutateAsync } = useCustomMutation<{ submissionId: string; recorded: boolean }>();

  const set = (fieldId: string, value: string | boolean | string[] | undefined) =>
    setDraft((d) => {
      const next = { ...d };
      if (value === undefined) delete next[fieldId];
      else next[fieldId] = value;
      return next;
    });

  const submit = async () => {
    if (!form || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const res = await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          operation: 'submitWorksheet',
          fields: ['assignmentId', 'submissionId', 'recorded'],
          variables: {
            input: {
              name: 'input',
              type: 'SubmitWorksheetInput!',
              value: {
                assignmentId: form.assignmentId,
                templateId: form.templateId,
                templateVersion: form.templateVersion,
                answers: fillAnswers(form.sections, draft),
              },
            },
          },
        },
      });
      if (!res?.data?.submissionId) {
        setProblem(t('worksheets.fill.failed'));
        return;
      }
      onDone();
    } catch (error) {
      const info = graphqlErrorInfo(error);
      if (info.code && info.message) {
        setProblem(info.message);
      } else {
        Sentry.captureException(error);
        setProblem(t('worksheets.fill.failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const field = (f: WorksheetFieldData) => {
    const v = draft[f.fieldId];
    const id = `worksheet-fill-field-${f.fieldId}`;
    switch (f.type) {
      case 'text':
        return (
          <Input
            data-testid={id}
            value={typeof v === 'string' ? v : ''}
            onChange={(e) => set(f.fieldId, e.target.value)}
          />
        );
      case 'longText':
        return (
          <Input.TextArea
            data-testid={id}
            rows={3}
            value={typeof v === 'string' ? v : ''}
            onChange={(e) => set(f.fieldId, e.target.value)}
          />
        );
      case 'number':
        return (
          <Input
            data-testid={id}
            inputMode="decimal"
            style={{ maxWidth: 160 }}
            value={typeof v === 'string' ? v : ''}
            onChange={(e) => set(f.fieldId, e.target.value)}
          />
        );
      case 'scale': {
        const steps = scaleSteps(f);
        return steps ? (
          <Radio.Group
            optionType="button"
            value={typeof v === 'string' ? v : undefined}
            onChange={(e) => set(f.fieldId, e.target.value)}
            data-testid={id}
          >
            {steps.map((n) => (
              <Radio.Button key={n} value={String(n)} data-testid={`worksheet-fill-option-${f.fieldId}-${n}`}>
                {n}
              </Radio.Button>
            ))}
          </Radio.Group>
        ) : (
          <Input
            data-testid={id}
            inputMode="decimal"
            style={{ maxWidth: 160 }}
            placeholder={`${f.min ?? 0}–${f.max ?? 10}`}
            value={typeof v === 'string' ? v : ''}
            onChange={(e) => set(f.fieldId, e.target.value)}
          />
        );
      }
      case 'singleChoice':
        return (
          <Radio.Group
            value={typeof v === 'string' ? v : undefined}
            onChange={(e) => set(f.fieldId, e.target.value)}
            data-testid={id}
          >
            <Space direction="vertical">
              {(f.options ?? []).map((o, i) => (
                <Radio key={o} value={o} data-testid={`worksheet-fill-option-${f.fieldId}-${i}`}>
                  {o}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );
      case 'multiChoice':
        return (
          <Checkbox.Group
            value={Array.isArray(v) ? v : []}
            onChange={(vals) => set(f.fieldId, vals as string[])}
            data-testid={id}
          >
            <Space direction="vertical">
              {(f.options ?? []).map((o, i) => (
                <Checkbox key={o} value={o} data-testid={`worksheet-fill-option-${f.fieldId}-${i}`}>
                  {o}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        );
      case 'date':
        return (
          <DateFnsDatePicker
            locale={pickerLocale}
            format="yyyy-MM-dd"
            value={typeof v === 'string' && v ? parse(v, 'yyyy-MM-dd', new Date()) : null}
            onChange={(d: Date | null) => set(f.fieldId, d ? format(d, 'yyyy-MM-dd') : undefined)}
            data-testid={id}
          />
        );
      case 'checkbox':
        return (
          <Checkbox
            data-testid={id}
            checked={v === true}
            onChange={(e) => set(f.fieldId, e.target.checked)}
          >
            {f.label}
            {f.required ? ' *' : ''}
          </Checkbox>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={!!assignmentId}
      onCancel={onClose}
      width={720}
      destroyOnClose
      title={t('worksheets.fill.title', { name: clientName })}
      footer={[
        <Button key="cancel" onClick={onClose} data-testid="worksheet-fill-cancel">
          {t('worksheets.fill.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={busy}
          disabled={!form}
          onClick={submit}
          data-testid="worksheet-fill-submit"
        >
          {t('worksheets.form.submit')}
        </Button>,
      ]}
      data-testid="worksheet-fill"
    >
      {isFetching && !form ? <Spin /> : null}
      {isError || (!isFetching && assignmentId && !form) ? (
        <Alert type="error" showIcon message={t('worksheets.fill.notFound')} />
      ) : null}
      {form ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Title level={5} style={{ margin: 0 }} data-testid="worksheet-fill-name">
            {form.name}
          </Typography.Title>
          <Typography.Text type="secondary">{t('worksheets.fill.intro')}</Typography.Text>
          {form.signatureRequired ? (
            <Alert type="info" showIcon message={t('worksheets.fill.consentNote')} data-testid="worksheet-fill-consent-note" />
          ) : null}
          {form.sections.map((s) => (
            <div key={s.sectionId}>
              {s.title ? <Typography.Title level={5}>{s.title}</Typography.Title> : null}
              {s.text ? <Typography.Paragraph>{s.text}</Typography.Paragraph> : null}
              {s.video ? (
                <Typography.Paragraph type="secondary">
                  {t('worksheets.fill.video', { title: s.video.title })}
                </Typography.Paragraph>
              ) : null}
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {s.fields.map((f) => (
                  <div key={f.fieldId}>
                    {f.type === 'checkbox' ? null : (
                      <Typography.Text strong>
                        {f.label}
                        {f.required ? ' *' : ''}
                      </Typography.Text>
                    )}
                    <div style={{ marginTop: 6 }}>{field(f)}</div>
                  </div>
                ))}
              </Space>
            </div>
          ))}
          {problem ? (
            <Alert type="error" showIcon message={problem} data-testid="worksheet-fill-error" />
          ) : null}
        </Space>
      ) : null}
    </Modal>
  );
};
